import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ResolvedQRLink } from '../../interfaces/qr_links';

/* eslint-disable */

// A single node the guest has navigated to via the "Explore linked catalogs" feature.
// `url` is the client-side path (pathname + search) used to get back to this node.
export interface LinkedNavNode {
  qr: string;
  r_id: number;
  name: string;
  url: string;
}

interface LinkedNavState {
  linksByQr: { [key: string]: ResolvedQRLink[] };
  navStack: LinkedNavNode[];
}

const LOCAL_STORAGE_KEY = '__tt_linked_nav';

const safelyParseJSON = (localStorageKey: string) => {
  try {
    const data = localStorage.getItem(localStorageKey);
    return JSON.parse(data);
  } catch (error) {
    console.error(`Failed to parse data from ${localStorageKey}`, error);
    return null;
  }
};

const persisted = safelyParseJSON(LOCAL_STORAGE_KEY) ?? {};

const initialState: LinkedNavState = {
  linksByQr: persisted.linksByQr ?? {},
  navStack: persisted.navStack ?? [],
};

const persist = (state: LinkedNavState) => {
  localStorage.setItem(
    LOCAL_STORAGE_KEY,
    JSON.stringify({
      linksByQr: state.linksByQr,
      navStack: state.navStack,
    }),
  );
};

const linkedNav = createSlice({
  name: 'linkedNav',
  initialState,
  reducers: {
    setLinksForQr(
      state,
      action: PayloadAction<{ qr: string; links: ResolvedQRLink[] }>,
    ) {
      state.linksByQr[action.payload.qr] = action.payload.links;
      persist(state);
    },
    // Starts a brand-new root session (a genuine, un-linked QR scan) -- discards any
    // previously explored tree so stale linked catalogs don't leak across sessions.
    resetStackToRoot(state, action: PayloadAction<LinkedNavNode>) {
      state.navStack = [action.payload];
      state.linksByQr = {};
      persist(state);
    },
    pushNode(state, action: PayloadAction<LinkedNavNode>) {
      state.navStack.push(action.payload);
      persist(state);
    },
    // Truncates the stack back to (and including) the node matching `qr`. Used both when
    // the guest taps a breadcrumb ancestor, and when a linked catalog links back to a qr
    // that's already on the stack (cycle handling) -- avoids unbounded stack growth.
    popToNode(state, action: PayloadAction<string>) {
      const idx = state.navStack.findIndex(n => n.qr === action.payload);
      if (idx !== -1) {
        state.navStack = state.navStack.slice(0, idx + 1);
      }
      persist(state);
    },
  },
});

export const {
  setLinksForQr,
  resetStackToRoot,
  pushNode,
  popToNode,
} = linkedNav.actions;

export default linkedNav.reducer;
